import { useState, useEffect } from 'react';
import { Volume2, VolumeX, Music } from 'lucide-react';
import { getAmbientEngine, AmbientType } from '@/hooks/useAmbientAudio';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';

interface AmbientMusicControlProps {
  category?: AmbientType;
  className?: string;
}

const TRACK_OPTIONS: { id: AmbientType; labelKey: string; icon: string }[] = [
  { id: 'default', labelKey: 'ambient_default', icon: '🌙' },
  { id: 'night', labelKey: 'ambient_night', icon: '🌃' },
  { id: 'scary', labelKey: 'ambient_scary', icon: '👻' },
  { id: 'truth', labelKey: 'ambient_truth', icon: '✨' },
  { id: 'identity', labelKey: 'ambient_identity', icon: '🪞' },
  { id: 'mortality', labelKey: 'ambient_mortality', icon: '⏳' },
  { id: 'nothing', labelKey: 'ambient_nothing', icon: '🕳️' },
  { id: 'meta', labelKey: 'ambient_meta', icon: '🔮' },
  { id: 'daily', labelKey: 'ambient_daily', icon: '☀️' },
  { id: 'seasonal', labelKey: 'ambient_seasonal', icon: '❄️' },
  { id: 'premium', labelKey: 'ambient_premium', icon: '💎' },
];

const AmbientMusicControl = ({ category, className = '' }: AmbientMusicControlProps) => {
  const { t } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(15);
  const [selectedTrack, setSelectedTrack] = useState<AmbientType>(category || 'default');
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    const wasEnabled = localStorage.getItem('ambient-audio-enabled') === 'true';
    const savedVolume = localStorage.getItem('ambient-audio-volume');
    const savedTrack = localStorage.getItem('ambient-audio-track') as AmbientType;
    
    if (savedVolume) {
      setVolume(parseInt(savedVolume, 10));
    }
    if (savedTrack) {
      setSelectedTrack(savedTrack);
    }
    if (wasEnabled && hasInteracted) {
      const engine = getAmbientEngine();
      engine.setVolume(volume / 100);
      engine.start(savedTrack || category || 'default');
      setIsPlaying(true);
    }
  }, [category, hasInteracted]);

  useEffect(() => {
    if (isPlaying) {
      const engine = getAmbientEngine();
      engine.setVolume(volume / 100);
    }
  }, [volume, isPlaying]);

  useEffect(() => {
    if (isPlaying) {
      const engine = getAmbientEngine();
      engine.start(selectedTrack);
    }
  }, [selectedTrack, isPlaying]);

  const togglePlay = () => {
    setHasInteracted(true);
    const engine = getAmbientEngine();
    
    if (isPlaying) {
      engine.stop();
      setIsPlaying(false);
      localStorage.setItem('ambient-audio-enabled', 'false');
    } else {
      engine.setVolume(volume / 100);
      engine.start(selectedTrack);
      setIsPlaying(true);
      localStorage.setItem('ambient-audio-enabled', 'true');
    }
  };

  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);
    localStorage.setItem('ambient-audio-volume', String(newVolume));
  };

  const handleTrackChange = (track: AmbientType) => {
    setSelectedTrack(track);
    localStorage.setItem('ambient-audio-track', track);
  };

  const getTrackLabel = (labelKey: string) => {
    const labels: Record<string, string> = {
      ambient_default: t('ambient_default') || 'Calm',
      ambient_night: t('ambient_night') || 'Night',
      ambient_scary: t('ambient_scary') || 'Eerie',
      ambient_truth: t('ambient_truth') || 'Truth',
      ambient_identity: t('ambient_identity') || 'Reflection',
      ambient_mortality: t('ambient_mortality') || 'Time',
      ambient_nothing: t('ambient_nothing') || 'Void',
      ambient_meta: t('ambient_meta') || 'Digital',
      ambient_daily: t('ambient_daily') || 'Warm',
      ambient_seasonal: t('ambient_seasonal') || 'Magical',
      ambient_premium: t('ambient_premium') || 'Deep',
    };
    return labels[labelKey] || labelKey;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`p-2 transition-all duration-300 hover:opacity-80 ${className}`}
          title={t('ambient_music') || 'Ambient Music'}
          aria-label={t('ambient_music') || 'Ambient Music'}
        >
          {isPlaying ? (
            <Volume2 className="w-4 h-4 text-muted-foreground" />
          ) : (
            <VolumeX className="w-4 h-4 text-muted-foreground/50" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-4" align="end">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Music className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">{t('ambient_music') || 'Ambient Music'}</span>
            </div>
            <Button
              variant={isPlaying ? 'default' : 'outline'}
              size="sm"
              onClick={togglePlay}
              className="h-7 px-2 text-xs"
            >
              {isPlaying ? (t('playing') || 'Playing') : (t('play') || 'Play')}
            </Button>
          </div>

          {/* Volume Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{t('volume') || 'Volume'}</span>
              <span className="text-xs text-muted-foreground">{volume}%</span>
            </div>
            <Slider
              value={[volume]}
              onValueChange={handleVolumeChange}
              max={100}
              min={0}
              step={1}
              className="w-full"
            />
          </div>

          {/* Track Selection */}
          <div className="space-y-2">
            <span className="text-xs text-muted-foreground">{t('ambient_track') || 'Ambient Track'}</span>
            <div className="grid grid-cols-3 gap-1.5">
              {TRACK_OPTIONS.map((track) => (
                <button
                  key={track.id}
                  onClick={() => handleTrackChange(track.id)}
                  className={`
                    flex flex-col items-center gap-0.5 p-2 rounded-md text-xs transition-all
                    ${selectedTrack === track.id 
                      ? 'bg-primary/20 text-primary ring-1 ring-primary/30' 
                      : 'bg-secondary/50 hover:bg-secondary text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  <span className="text-base">{track.icon}</span>
                  <span className="truncate w-full text-center text-[10px]">
                    {getTrackLabel(track.labelKey)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AmbientMusicControl;
